import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  Clock, 
  CheckCircle, 
  FileText,
  Play,
  Edit,
  Trash2
} from 'lucide-react';
import { 
  Workflow, 
  WorkflowType, 
  WorkflowStatus,
  WorkflowFunction,
  workflowApi,
  sessionApi
} from '../services/api';
import { WorkflowExecutionModal } from '../components/WorkflowExecutionModal';
import { WorkflowDetailsModal } from '../components/WorkflowDetailsModal';
import { DraftPreviewModal } from '../components/DraftPreviewModal';
import './WorkflowLibrary.css';

interface WorkflowLibraryProps {
  onNavigate?: (item: string, options?: { sessionId?: string, openWorkflowSidebar?: boolean, workflowData?: any }) => void;
}

const WorkflowLibrary: React.FC<WorkflowLibraryProps> = ({ onNavigate }) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<WorkflowType | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<WorkflowStatus | 'all'>('all');
  const [selectedFunction, setSelectedFunction] = useState<string>('all');
  const [functions, setFunctions] = useState<WorkflowFunction[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [workflowCounts, setWorkflowCounts] = useState({ published: 0, drafts: 0 });
  
  // Sorting states
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Modal states
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDraftPreviewModal, setShowDraftPreviewModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<any>(null);
  const [executingWorkflow, setExecutingWorkflow] = useState<Workflow | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(false);

  // Initialize session only once on mount
  useEffect(() => {
    initializeSession();
    loadWorkflowCounts();
  }, []);
  
  // Load data when filters or tab changes
  useEffect(() => {
    loadWorkflows();
    loadFunctions();
  }, [activeTab, selectedStatus, selectedFunction, searchQuery]);

  const initializeSession = async () => {
    // No longer need to initialize a session here since we create new sessions per execution
    // This function can be removed or simplified
    console.log('WorkflowLibrary: Ready for workflow executions');
  };

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const params: any = {
        view_mode: activeTab  // Use the active tab as view_mode
      };
      
      // Only apply status filter for appropriate tabs
      if (activeTab === 'published') {
        // For published tab, status is already filtered by view_mode
      } else if (activeTab === 'drafts') {
        // For drafts tab, allow status filtering within drafts
        if (selectedStatus !== 'all' && selectedStatus !== 'published') {
          params.status = selectedStatus;
        }
      }
      
      if (selectedFunction !== 'all') params.function_category_id = selectedFunction;
      if (searchQuery) params.search = searchQuery;

      const response = await workflowApi.list(params);
      setWorkflows(response.data.workflows);
    } catch (error) {
      console.error('Failed to load workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowCounts = async () => {
    try {
      const response = await workflowApi.getCounts();
      setWorkflowCounts(response.data);
    } catch (error) {
      console.error('Failed to load workflow counts:', error);
    }
  };

  const loadFunctions = async () => {
    try {
      const response = await workflowApi.listFunctions();
      setFunctions(response.data);
    } catch (error) {
      console.error('Failed to load functions:', error);
    }
  };

  const handleWorkflowClick = async (workflow: Workflow) => {
    if (workflow.type === 'draft') {
      // Show preview modal for draft workflows
      setLoadingDraft(true);
      try {
        // Fetch full draft details including draft_state
        const response = await workflowApi.get(workflow.id);
        const details = response.data;
        
        // Prepare draft data for preview modal
        const draftData = {
          id: workflow.id,
          name: workflow.name,
          description: workflow.description,
          current_phase: details.draft_state?.ui_state?.currentPhase || 1,
          files: details.draft_state?.ui_state?.uploadedFiles || [],
          last_saved: workflow.updated_at || workflow.created_at,
          generated_scripts: details.content?.scripts || [],
          requirements_analysis: details.draft_state?.ui_state?.requirementsAnalysis || '',
        };
        
        setSelectedDraft(draftData);
        setShowDraftPreviewModal(true);
      } catch (error) {
        console.error('Failed to load draft details:', error);
        alert('Failed to load draft details. Please try again.');
      } finally {
        setLoadingDraft(false);
      }
    } else {
      // Show workflow details modal for final workflows
      setSelectedWorkflow(workflow);
      setShowDetailsModal(true);
    }
  };

  const handleDraftRestore = async () => {
    if (!selectedDraft) return;
    
    try {
      setLoadingDraft(true);
      const response = await workflowApi.restore(selectedDraft.id);
      const { session_id, session_state, ui_state } = response.data;
      
      // Store restoration context in localStorage for the chat to pick up
      const restorationContext = {
        sessionId: session_id,
        draftRestored: true,
        workflowName: selectedDraft.name,
        currentPhase: selectedDraft.current_phase,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('draftRestorationContext', JSON.stringify(restorationContext));
      
      // Close modal and navigate to chat
      setShowDraftPreviewModal(false);
      setSelectedDraft(null);
      
      // Navigate to chat with the new session
      if (onNavigate) {
        onNavigate('launchpad', { 
          sessionId: session_id,
          openWorkflowSidebar: true
        });
      } else {
        // Fallback to page reload if no navigation handler
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Failed to restore draft:', error);
      alert('Failed to restore draft workflow. Please try again.');
    } finally {
      setLoadingDraft(false);
    }
  };

  const handleExecuteWorkflow = async (workflow: Workflow, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (workflow.type !== 'final' || workflow.status !== 'published') {
      alert('Only published final workflows can be executed');
      return;
    }
    
    try {
      // Create a new session for this workflow execution
      const response = await sessionApi.create();
      const newSession = response.data;
      
      // Update session to workflow mode
      await sessionApi.update(newSession.session_id, {
        title: `Execution: ${workflow.name}`,
        mode: 'workflow'
      });
      
      // Store the new session ID for the execution modal
      setCurrentSessionId(newSession.session_id);
      setExecutingWorkflow(workflow);
      setShowExecutionModal(true);
    } catch (error) {
      console.error('Failed to create new session for workflow execution:', error);
      alert('Failed to initialize new session. Please try again.');
    }
  };

  const handleExecutionStart = (executionId: string) => {
    // Close the modal
    setShowExecutionModal(false);
    
    // Store workflow execution context for the launchpad to pick up
    if (currentSessionId && executingWorkflow) {
      const executionContext = {
        sessionId: currentSessionId,
        executionId: executionId,
        workflowId: executingWorkflow.id,
        workflowName: executingWorkflow.name,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('pendingWorkflowExecution', JSON.stringify(executionContext));
    }
    
    setExecutingWorkflow(null);
    
    // Show brief success message
    alert(`Workflow execution started successfully!\nExecution ID: ${executionId}\n\nTaking you to the Workflow sidebar...`);
    
    // Navigate to Launchpad with the current session and open workflow sidebar
    if (onNavigate && currentSessionId) {
      onNavigate('launchpad', { 
        sessionId: currentSessionId, 
        openWorkflowSidebar: true,
        workflowData: {
          workflowId: executingWorkflow?.id,
          workflowName: executingWorkflow?.name,
          executionId: executionId
        }
      });
    }
    
    // Reload workflows to update execution count  
    loadWorkflows();
  };

  const handleEditAsDraft = (newSessionId: string, workflowContext: any) => {
    // Store workflow context in localStorage for the chat to pick up
    try {
      localStorage.setItem('workflowEditContext', JSON.stringify({
        sessionId: newSessionId,
        context: workflowContext,
        timestamp: new Date().toISOString()
      }));
    } catch {
      // Still navigate even if localStorage fails
      console.warn('Failed to store workflow context in localStorage');
    }
    
    // Navigate to launchpad/chat - in a real app, use proper routing
    window.location.href = '/';
  };

  const handleExecuteFromDetails = async () => {
    if (selectedWorkflow) {
      try {
        // Create a new session for this workflow execution
        const response = await sessionApi.create();
        const newSession = response.data;
        
        // Update session to workflow mode
        await sessionApi.update(newSession.session_id, {
          title: `Execution: ${selectedWorkflow.name}`,
          mode: 'workflow'
        });
        
        // Store the new session ID for the execution modal
        setCurrentSessionId(newSession.session_id);
        setExecutingWorkflow(selectedWorkflow);
        setShowDetailsModal(false);
        setShowExecutionModal(true);
      } catch (error) {
        console.error('Failed to create new session for workflow execution:', error);
        alert('Failed to initialize new session. Please try again.');
      }
    }
  };

  const handleDeleteWorkflow = async (workflow: Workflow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${workflow.name}"?`)) {
      return;
    }

    try {
      await workflowApi.delete(workflow.id);
      loadWorkflows();
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      alert('Failed to delete workflow');
    }
  };

  const getStatusIcon = (workflow: Workflow) => {
    if (workflow.type === 'draft') {
      return <Clock className="status-icon draft" size={16} />;
    }
    if (workflow.status === 'published') {
      return <CheckCircle className="status-icon published" size={16} />;
    }
    return <FileText className="status-icon" size={16} />;
  };

  const getStatusLabel = (workflow: Workflow) => {
    if (workflow.type === 'draft') return 'Draft';
    return workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortWorkflows = (workflows: Workflow[]) => {
    return [...workflows].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = getStatusLabel(a).toLowerCase();
          bValue = getStatusLabel(b).toLowerCase();
          break;
        case 'function':
          aValue = a.function_category_name?.toLowerCase() || '';
          bValue = b.function_category_name?.toLowerCase() || '';
          break;
        case 'runs':
          aValue = a.execution_count || 0;
          bValue = b.execution_count || 0;
          break;
        case 'updated_at':
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  return (
    <div className="workflow-library">
      <div className="library-header">
        <h1>Workflow Library</h1>
        <div className="header-actions">
          <button 
            className={`view-toggle ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            ⊞
          </button>
          <button 
            className={`view-toggle ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
            title="Table view"
          >
            ☰
          </button>
        </div>
      </div>

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'published' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('published');
            setSelectedStatus('all');
            loadWorkflowCounts();
          }}
        >
          <CheckCircle size={16} className="tab-icon" />
          <span>Published</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'drafts' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('drafts');
            setSelectedStatus('all');
            loadWorkflowCounts();
          }}
        >
          <FileText size={16} className="tab-icon" />
          <span>My Drafts</span>
          {workflowCounts.drafts > 0 && (
            <span className="tab-badge">{workflowCounts.drafts}</span>
          )}
        </button>
      </div>

      <div className="library-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button 
          className="filter-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          <span>Filters</span>
          <ChevronDown size={14} className={showFilters ? 'rotated' : ''} />
        </button>
      </div>

      {showFilters && (
        <div className="filter-panel">
          {activeTab === 'drafts' && (
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value as WorkflowStatus | 'all')}
              >
                <option value="all">All Drafts</option>
                <option value="draft">In Progress</option>
                <option value="pending_approval">Pending Approval</option>
              </select>
            </div>
          )}
          
          {activeTab === 'published' && (
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value as WorkflowStatus | 'all')}
              >
                <option value="all">All Published</option>
                <option value="published">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Function</label>
            <select 
              value={selectedFunction} 
              onChange={(e) => setSelectedFunction(e.target.value)}
            >
              <option value="all">All Functions</option>
              {functions.map(func => (
                <option key={func.id} value={func.id}>
                  {func.name} ({func.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="workflow-content">
        {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading workflows...</p>
        </div>
      ) : workflows.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <h3>
            {activeTab === 'published' 
              ? 'No published workflows yet' 
              : 'No draft workflows'}
          </h3>
          <p>
            {searchQuery || selectedStatus !== 'all' || selectedFunction !== 'all'
              ? 'Try adjusting your filters'
              : activeTab === 'published'
                ? 'Finalize a workflow to see it here. Published workflows are reviewed and ready for team use.'
                : 'Save your work in progress from the chat to see it here.'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="workflow-grid">
          {workflows.map(workflow => (
            <div 
              key={workflow.id} 
              className={`workflow-card ${workflow.type === 'final' && workflow.status === 'published' ? 'published' : 'draft'}`}
              onClick={() => handleWorkflowClick(workflow)}
            >
              <div className="card-header">
                <div className="card-title">
                  <h3>{workflow.name}</h3>
                  {getStatusIcon(workflow)}
                </div>
                <div className="card-actions">
                  {workflow.type === 'final' && workflow.status === 'published' && (
                    <button 
                      className="icon-button"
                      onClick={(e) => handleExecuteWorkflow(workflow, e)}
                      title="Execute workflow"
                    >
                      <Play size={16} />
                    </button>
                  )}
                  <button 
                    className="icon-button"
                    onClick={(e) => handleDeleteWorkflow(workflow, e)}
                    title="Delete workflow"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              {workflow.description && (
                <p className="card-description">{workflow.description}</p>
              )}
              
              <div className="card-meta">
                <span className={`meta-item status-badge ${workflow.type === 'final' && workflow.status === 'published' ? 'prod' : 'draft'}`}>
                  {workflow.type === 'final' && workflow.status === 'published' ? '✓ Production' : '📝 Draft'}
                </span>
                {workflow.function_category_name && (
                  <span className="meta-item">
                    {workflow.function_category_name}
                  </span>
                )}
                <span className="meta-item">
                  {new Date(workflow.created_at).toLocaleDateString()}
                </span>
              </div>

              {workflow.type === 'final' && workflow.status === 'published' ? (
                <div className="card-stats">
                  {workflow.has_uat && (
                    <span className="stat-item uat">
                      <CheckCircle size={14} />
                      UAT Complete
                    </span>
                  )}
                  {workflow.execution_count > 0 && (
                    <span className="stat-item">
                      Used by {workflow.execution_count} team members
                    </span>
                  )}
                </div>
              ) : (
                <div className="card-warning">
                  ⚠️ Personal draft - not reviewed
                </div>
              )}
            </div>
          ))}
        </div>
        ) : (
          <div className="workflow-table">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className={sortColumn === 'name' ? 'sorted' : ''}>
                  Name
                  {sortColumn === 'name' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th onClick={() => handleSort('status')} className={sortColumn === 'status' ? 'sorted' : ''}>
                  Status
                  {sortColumn === 'status' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th onClick={() => handleSort('function')} className={sortColumn === 'function' ? 'sorted' : ''}>
                  Function
                  {sortColumn === 'function' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th onClick={() => handleSort('runs')} className={sortColumn === 'runs' ? 'sorted' : ''}>
                  Runs
                  {sortColumn === 'runs' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th onClick={() => handleSort('updated_at')} className={sortColumn === 'updated_at' ? 'sorted' : ''}>
                  Last Updated
                  {sortColumn === 'updated_at' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th onClick={() => handleSort('created_at')} className={sortColumn === 'created_at' ? 'sorted' : ''}>
                  Created
                  {sortColumn === 'created_at' && (
                    <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortWorkflows(workflows).map(workflow => (
                <tr 
                  key={workflow.id}
                  onClick={() => handleWorkflowClick(workflow)}
                >
                  <td className="name-cell">
                    {getStatusIcon(workflow)}
                    <span>{workflow.name}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${workflow.status}`}>
                      {getStatusLabel(workflow)}
                    </span>
                  </td>
                  <td>{workflow.function_category_name || '-'}</td>
                  <td>{workflow.execution_count || 0}</td>
                  <td>{workflow.updated_at ? new Date(workflow.updated_at).toLocaleDateString() : '-'}</td>
                  <td>{new Date(workflow.created_at).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    {workflow.status === 'published' && (
                      <button 
                        className="icon-button"
                        onClick={(e) => handleExecuteWorkflow(workflow, e)}
                        title="Execute workflow"
                      >
                        <Play size={16} />
                      </button>
                    )}
                    <button 
                      className="icon-button"
                      onClick={(e) => handleDeleteWorkflow(workflow, e)}
                      title="Delete workflow"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      
      {/* Workflow Execution Modal */}
      {showExecutionModal && executingWorkflow && currentSessionId && (
        <WorkflowExecutionModal
          isOpen={showExecutionModal}
          onClose={() => {
            setShowExecutionModal(false);
            setExecutingWorkflow(null);
          }}
          workflowId={executingWorkflow.id}
          workflowName={executingWorkflow.name}
          sessionId={currentSessionId}
          onExecutionStart={handleExecutionStart}
        />
      )}
      
      {/* Workflow Details Modal */}
      {showDetailsModal && selectedWorkflow && (
        <WorkflowDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedWorkflow(null);
          }}
          workflowId={selectedWorkflow.id}
          onExecute={handleExecuteFromDetails}
          onEditAsDraft={handleEditAsDraft}
        />
      )}
      
      {/* Draft Preview Modal */}
      {showDraftPreviewModal && selectedDraft && (
        <DraftPreviewModal
          isOpen={showDraftPreviewModal}
          onClose={() => {
            setShowDraftPreviewModal(false);
            setSelectedDraft(null);
          }}
          onRestore={handleDraftRestore}
          draft={selectedDraft}
        />
      )}
      
      {/* Loading overlay for draft operations */}
      {loadingDraft && (
        <div className="modal-overlay">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading draft details...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowLibrary;